/**
 * ApplicationWorkspace — the ONE canonical review surface for an application, over the single
 * job_applications record (route /applications/:applicationId). Reached from the seeker "In
 * Motion" pipeline and the employer Applications tab. Role-aware; the backend authorizes and
 * shapes the payload. Designed for a hiring manager: decision context + disposition actions on
 * top, then the readable application package (Resume | Cover Letter | Activity).
 */

import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  ArrowLeft, Loader2, FileText, MessageSquare, GraduationCap, CheckCircle2,
  Circle, MapPin, Building2, Download, Save, Info, Calendar, Clock, Mail, Phone,
} from 'lucide-react';
import { stageLabel } from '@/lib/applicationStage';
import { SubmittedResumeView } from '@/components/resume/SubmittedResumeView';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMP_STAGE = {
  started: 'Applied', submitted: 'Applied', contacted: 'Reviewing',
  interview: 'Interviewing', offer: 'Offer', hired: 'Hired',
  not_selected: 'Not Selected', withdrawn: 'Withdrawn',
};

const DISPOSITIONS = [
  { action: 'reviewing', label: 'Reviewing' },
  { action: 'interview', label: 'Interview' },
  { action: 'offer', label: 'Offer' },
  { action: 'hired', label: 'Hire', primary: true },
  { action: 'not_selected', label: 'Not Selected', ghost: true },
];

function fmtDate(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return null; }
}

function agingLabel(ws) {
  if (!ws?.needs_response) return null;
  const iso = ws.last_activity_at || ws.applied_at;
  if (!iso) return 'New';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return days <= 0 ? 'New' : `${days} day${days === 1 ? '' : 's'} waiting`;
}

// Render the SUBMITTED cover letter as a professional business letter that visually belongs to
// the same package as the submitted resume: candidate header/contact treatment mirrors
// SubmittedResumeView, clean readable width, preserved paragraph breaks. Uses ONLY immutable
// submitted-snapshot + job-snapshot data — never fabricates addressee/employer address.
function StyledCoverLetter({ text, resume, appliedAt, candidateName }) {
  const pi = (resume && resume.personal_info) || {};
  const name = pi.full_name || candidateName || 'Candidate';
  const dateStr = fmtDate(appliedAt);
  const paras = String(text).split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
  return (
    <div className="mx-auto max-w-[46rem] bg-white" data-testid="workspace-cover-letter-view">
      {/* Candidate letterhead — mirrors the submitted résumé header treatment */}
      <div className="border-b border-slate-200 pb-4 mb-6">
        <h3 className="text-2xl font-bold text-slate-900">{name}</h3>
        <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-sm text-slate-600">
          {pi.city && <span className="flex items-center gap-1"><MapPin className="w-4 h-4 flex-shrink-0" /> {pi.city}{pi.state ? `, ${pi.state}` : ''}</span>}
          {pi.email && <span className="flex items-center gap-1"><Mail className="w-4 h-4 flex-shrink-0" /> {pi.email}</span>}
          {pi.phone && <span className="flex items-center gap-1"><Phone className="w-4 h-4 flex-shrink-0" /> {pi.phone}</span>}
        </div>
      </div>
      {dateStr && <p className="text-sm text-slate-500 mb-5" data-testid="cover-letter-date">{dateStr}</p>}
      <div className="space-y-4">
        {paras.map((p, i) => (
          <p key={i} className="text-[15px] text-slate-700 leading-[1.75] whitespace-pre-line">{p}</p>
        ))}
      </div>
    </div>
  );
}

export default function ApplicationWorkspace() {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const [ws, setWs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('resume');
  const [clText, setClText] = useState('');
  const [savingCl, setSavingCl] = useState(false);
  const [dispatching, setDispatching] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await axios.get(`${API}/applications/${applicationId}/workspace`, { withCredentials: true });
      setWs(res.data);
      if (res.data?.can_edit_cover_letter) {
        const cl = await axios.get(`${API}/applications/${applicationId}/cover-letter`, { withCredentials: true });
        setClText(cl.data?.text || '');
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Unable to load this application.');
    } finally {
      setLoading(false);
    }
  }, [applicationId]);

  useEffect(() => { load(); }, [load]);

  const saveCoverLetter = async () => {
    setSavingCl(true);
    try {
      await axios.put(`${API}/applications/${applicationId}/cover-letter`, { text: clText }, { withCredentials: true });
      toast.success('Cover letter saved for this application');
      await load();
    } catch { toast.error('Could not save cover letter'); }
    finally { setSavingCl(false); }
  };

  const disposition = async (action) => {
    setDispatching(action);
    try {
      await axios.put(`${API}/employer/applications/${applicationId}/disposition`, { action }, { withCredentials: true });
      toast.success('Application updated');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update'); }
    finally { setDispatching(null); }
  };

  const downloadEmployerResume = async () => {
    try {
      const res = await axios.get(`${API}/employer/applications/${applicationId}/resume/pdf`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'Submitted_Resume.pdf'; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Resume not available'); }
  };

  const downloadCoverLetter = async () => {
    try {
      const res = await axios.get(`${API}/employer/applications/${applicationId}/cover-letter/pdf`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'Submitted_Cover_Letter.pdf'; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Cover letter not available'); }
  };

  if (loading) {
    return <Layout><div className="flex items-center justify-center py-32" data-testid="workspace-loading"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div></Layout>;
  }
  if (error || !ws) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto py-16 px-4 text-center" data-testid="workspace-error">
          <p className="text-slate-600 mb-4">{error || 'Application not found.'}</p>
          <Button variant="outline" onClick={() => navigate(-1)}>Go back</Button>
        </div>
      </Layout>
    );
  }

  const isEmployer = ws.role === 'employer';
  // Explicit, whitelisted return context — never an arbitrary redirect URL.
  const origin = search.get('origin');
  const originJob = search.get('job');
  const originStage = search.get('stage');
  const goBack = () => {
    if (origin === 'job' && originJob) {
      navigate(`/jobs/${originJob}`);
    } else if (origin === 'apps' && isEmployer) {
      const p = new URLSearchParams({ tab: 'applications' });
      if (originJob) p.set('job', originJob);
      if (originStage) p.set('stage', originStage);
      navigate(`/employer/dashboard?${p.toString()}`);
    } else {
      navigate(isEmployer ? '/employer/dashboard' : '/dashboard');
    }
  };
  const stage = isEmployer ? (EMP_STAGE[ws.current_lifecycle_state] || 'Applied') : stageLabel(ws);
  const isTerminal = ['hired', 'not_selected', 'withdrawn'].includes(ws.current_lifecycle_state);
  const sourceTag = ws.is_skillbridge ? 'SkillBridge' : ws.is_external ? 'External' : null;
  const cl = ws.documents?.job_specific_cover_letter;
  const aging = agingLabel(ws);
  const headline = isEmployer ? (ws.applicant_name || 'Applicant') : ws.opportunity_title;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="application-workspace">
        <button
          type="button"
          onClick={goBack}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-5"
          data-testid="workspace-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        {/* Decision header */}
        <Card className="p-6 rounded-2xl border border-slate-200 mb-6" data-testid="workspace-header">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h1 className="text-2xl font-bold text-slate-900" data-testid="workspace-title">{headline}</h1>
                {sourceTag && (
                  <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400 border border-slate-200 rounded px-1.5 py-0.5">{sourceTag}</span>
                )}
              </div>
              <div className="flex items-center gap-x-4 gap-y-1 text-sm text-slate-500 flex-wrap">
                <span className="inline-flex items-center gap-1">
                  <Building2 className="w-4 h-4" />
                  {isEmployer ? ws.opportunity_title : (ws.employer_or_provider || ws.opportunity_title)}
                </span>
                {ws.location && <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" />{ws.location}</span>}
                {fmtDate(ws.applied_at) && <span className="inline-flex items-center gap-1"><Calendar className="w-4 h-4" />Applied {fmtDate(ws.applied_at)}</span>}
              </div>
            </div>
            <div className="flex flex-col items-end gap-1.5">
              <span className="inline-flex items-center gap-1 text-brand bg-brand-light rounded-full px-3 py-1 text-sm font-semibold" data-testid="workspace-stage">{stage}</span>
              {aging && <span className="inline-flex items-center gap-1 text-[12px] text-amber-600 font-medium" data-testid="workspace-aging"><Clock className="w-3.5 h-3.5" />{aging}</span>}
            </div>
          </div>

          {/* Disposition actions (employer, native, non-terminal) */}
          {ws.can_disposition && !isTerminal && (
            <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-2 flex-wrap" data-testid="workspace-dispositions">
              {DISPOSITIONS.map((d) => (
                <Button
                  key={d.action}
                  size="sm"
                  variant={d.primary ? 'default' : d.ghost ? 'ghost' : 'outline'}
                  disabled={!!dispatching}
                  onClick={() => disposition(d.action)}
                  data-testid={`workspace-disposition-${d.action}`}
                  className={`h-8 text-xs rounded-lg ${d.primary ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : d.ghost ? 'text-slate-500 hover:text-slate-700' : 'border-slate-200'}`}
                >
                  {dispatching === d.action ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}{d.label}
                </Button>
              ))}
            </div>
          )}
        </Card>

        {ws.seeker_reported_only && (
          <div className="mb-6 flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800" data-testid="workspace-external-notice">
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>This application happened off Sergeant TAP. Statuses here are what you reported — we can't see the employer's responses.</span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Application package */}
          <div className="lg:col-span-2">
            <Card className="rounded-2xl border border-slate-200 overflow-hidden" data-testid="workspace-package">
              {/* Tabs */}
              <div className="flex border-b border-slate-200 px-2">
                {[['resume', 'Resume'], ['cover', 'Cover Letter'], ['activity', 'Activity']].map(([id, lbl]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setTab(id)}
                    data-testid={`workspace-tab-${id}`}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${tab === id ? 'border-brand text-brand' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
                  >
                    {lbl}
                  </button>
                ))}
              </div>

              <div className="p-6">
                {tab === 'resume' && (
                  <div data-testid="workspace-resume-tab">
                    {ws.resume_data ? (
                      <>
                        {isEmployer && (
                          <div className="flex justify-end mb-3">
                            <Button size="sm" variant="outline" className="border-slate-200" onClick={downloadEmployerResume} data-testid="workspace-download-resume">
                              <Download className="w-3.5 h-3.5 mr-1.5" /> PDF
                            </Button>
                          </div>
                        )}
                        <SubmittedResumeView resume={ws.resume_data} />
                        <p className="text-xs text-slate-400 mt-4">Submitted version — captured at application and unchanged by later profile edits.</p>
                      </>
                    ) : (
                      <p className="text-sm text-slate-500 py-8 text-center">No submitted resume was captured for this application.</p>
                    )}
                  </div>
                )}

                {tab === 'cover' && (
                  <div data-testid="workspace-cover-tab">
                    {ws.can_edit_cover_letter ? (
                      <div data-testid="workspace-cover-letter-editor">
                        <Textarea value={clText} onChange={(e) => setClText(e.target.value)} rows={12} placeholder="Write a cover letter for this application…" className="text-[15px]" data-testid="workspace-cover-letter-input" />
                        <div className="mt-3 flex justify-end">
                          <Button size="sm" onClick={saveCoverLetter} disabled={savingCl} className="bg-brand hover:bg-brand-hover text-white" data-testid="workspace-cover-letter-save">
                            {savingCl ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />} Save cover letter
                          </Button>
                        </div>
                      </div>
                    ) : cl?.available && cl?.text ? (
                      <>
                        {isEmployer && (
                          <div className="flex justify-end mb-3">
                            <Button size="sm" variant="outline" className="border-slate-200" onClick={downloadCoverLetter} data-testid="workspace-download-cover-letter">
                              <Download className="w-3.5 h-3.5 mr-1.5" /> PDF
                            </Button>
                          </div>
                        )}
                        <StyledCoverLetter
                          text={cl.text}
                          resume={ws.resume_data}
                          appliedAt={ws.applied_at}
                          candidateName={ws.applicant_name}
                        />
                      </>
                    ) : (
                      <p className="text-sm text-slate-500 py-8 text-center" data-testid="workspace-no-cover-letter">Cover letter not provided.</p>
                    )}
                  </div>
                )}

                {tab === 'activity' && (
                  <div data-testid="workspace-activity-tab">
                    {ws.history?.length ? (
                      <ol className="space-y-3">
                        {ws.history.map((h, i) => (
                          <li key={i} className="flex items-start gap-3" data-testid={`history-item-${i}`}>
                            {i === ws.history.length - 1 ? <CheckCircle2 className="w-4 h-4 text-brand mt-0.5 flex-shrink-0" /> : <Circle className="w-4 h-4 text-slate-300 mt-0.5 flex-shrink-0" />}
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-slate-800">{h.label}{h.reported && <span className="ml-1.5 text-[10px] text-slate-400 font-normal">· reported</span>}</p>
                              {fmtDate(h.at) && <p className="text-xs text-slate-400">{fmtDate(h.at)}</p>}
                            </div>
                          </li>
                        ))}
                      </ol>
                    ) : (
                      <p className="text-sm text-slate-500 py-8 text-center">No activity recorded yet.</p>
                    )}
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* Right rail: conversation + interview prep */}
          <div className="space-y-6">
            <Card className="p-6 rounded-2xl border border-slate-200" data-testid="workspace-conversation">
              <div className="flex items-center gap-2 mb-3">
                <MessageSquare className="w-5 h-5 text-brand" />
                <h2 className="text-base font-bold text-slate-900">Messages</h2>
              </div>
              {ws.conversation ? (
                <Button variant="outline" className="w-full" onClick={() => navigate(`/messages?conversation=${ws.conversation.conversation_id}`)} data-testid="workspace-open-conversation">Open conversation</Button>
              ) : (
                <p className="text-sm text-slate-500">No conversation yet for this application.</p>
              )}
            </Card>

            {ws.interview_prep?.applicable && !isEmployer && (
              <Card className="p-6 rounded-2xl border border-slate-200" data-testid="workspace-interview-prep">
                <div className="flex items-center gap-2 mb-3">
                  <GraduationCap className="w-5 h-5 text-brand" />
                  <h2 className="text-base font-bold text-slate-900">Interview prep</h2>
                </div>
                <p className="text-sm text-slate-500 mb-3">
                  Prepare for {ws.interview_prep.job_title || 'this role'}{ws.interview_prep.employer_or_provider ? ` at ${ws.interview_prep.employer_or_provider}` : ''}.
                </p>
                {ws.interview_prep.qualifications?.length > 0 && (
                  <div className="mb-3">
                    <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Your qualifications</p>
                    <div className="flex flex-wrap gap-1.5">
                      {ws.interview_prep.qualifications.map((q, i) => (
                        <span key={i} className="text-xs bg-slate-100 text-slate-600 rounded px-2 py-0.5">{q}</span>
                      ))}
                    </div>
                  </div>
                )}
                <Button variant="outline" className="w-full" onClick={() => navigate('/interview-readiness')} data-testid="workspace-interview-readiness-link">Go to Interview Readiness</Button>
              </Card>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
